import { useState } from 'react';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Tag } from 'lucide-react';
import storage from '../utils/storage';
import auth from '../utils/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';

export default function CategoriesPage() {
  const [categories, setCategories] = useState(storage.getCategories());
  const [products] = useState(storage.getProducts());
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', color: '#002FA7', defaultMarkup: 30 });
  
  const canEdit = auth.hasPermission('manager');

  const handleAdd = () => {
    if (!formData.name) {
      toast.error('Please enter category name');
      return;
    }
    if (categories.find(c => c.name === formData.name)) {
      toast.error('Category already exists');
      return;
    }

    const newCategory = {
      id: `cat-${Date.now()}`,
      name: formData.name,
      color: formData.color,
      defaultMarkup: parseInt(formData.defaultMarkup) || 0
    };

    const updated = [...categories, newCategory];
    setCategories(updated);
    storage.setCategories(updated);
    toast.success('Category added successfully');
    setShowAddDialog(false);
    setFormData({ name: '', color: '#002FA7', defaultMarkup: 30 });
  };

  const handleEdit = () => {
    if (!formData.name) {
      toast.error('Please enter category name');
      return;
    }

    const updated = categories.map(c => 
      c.id === selectedCategory.id 
        ? { ...c, name: formData.name, color: formData.color, defaultMarkup: parseInt(formData.defaultMarkup) || 0 }
        : c
    );
    setCategories(updated);
    storage.setCategories(updated);
    toast.success('Category updated successfully');
    setShowEditDialog(false);
    setSelectedCategory(null);
  };

  const handleDelete = () => {
    const updated = categories.filter(c => c.id !== selectedCategory.id);
    setCategories(updated);
    storage.setCategories(updated);
    toast.success('Category deleted successfully');
    setShowDeleteDialog(false);
    setSelectedCategory(null);
  };

  const getCategoryProductCount = (categoryName) => {
    return products.filter(p => p.category === categoryName).length;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-black font-heading tracking-tight">Categories</h1>
          <p className="text-muted-foreground mt-2">Manage product categories</p>
        </div>
        {canEdit && (
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-category-btn" className="font-bold uppercase tracking-wide">
                <Plus size={20} className="mr-2" />
                Add Category
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="text-2xl font-black font-heading">Add Category</DialogTitle>
                <DialogDescription>Create a new product category</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Category Name *</Label>
                  <Input
                    data-testid="category-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Electronics"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Color</Label>
                  <Input
                    type="color"
                    value={formData.color}
                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Default Markup (%)</Label>
                  <Input
                    type="number"
                    value={formData.defaultMarkup}
                    onChange={(e) => setFormData({ ...formData, defaultMarkup: e.target.value })}
                    placeholder="30"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-4">
                <Button variant="outline" onClick={() => setShowAddDialog(false)}>Cancel</Button>
                <Button data-testid="save-category-btn" onClick={handleAdd}>Save Category</Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {categories.map(category => (
          <Card key={category.id} className="card-hover" data-testid={`category-card-${category.id}`}>
            <CardHeader className="border-l-4" style={{ borderColor: category.color }}>
              <CardTitle className="flex items-center gap-2">
                <Tag size={20} style={{ color: category.color }} />
                {category.name}
              </CardTitle>
              <CardDescription>{getCategoryProductCount(category.name)} products</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-sm" style={{ backgroundColor: category.color }} />
                  <div>
                    <p className="text-sm text-muted-foreground">Color Code</p>
                    <p className="font-mono text-sm">{category.color}</p>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Default Markup</p>
                  <p className="font-bold">{category.defaultMarkup}%</p>
                </div>
                {canEdit && (
                  <div className="flex gap-2 pt-2 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => {
                        setSelectedCategory(category);
                        setFormData({ name: category.name, color: category.color, defaultMarkup: category.defaultMarkup });
                        setShowEditDialog(true);
                      }}
                      data-testid={`edit-category-${category.id}`}
                    >
                      <Edit size={16} className="mr-1" />
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1 text-destructive hover:text-destructive"
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowDeleteDialog(true);
                      }}
                      data-testid={`delete-category-${category.id}`}
                    >
                      <Trash2 size={16} className="mr-1" />
                      Delete
                    </Button>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-2xl font-black font-heading">Edit Category</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Color</Label>
              <Input
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Default Markup (%)</Label>
              <Input
                type="number"
                value={formData.defaultMarkup}
                onChange={(e) => setFormData({ ...formData, defaultMarkup: e.target.value })}
              />
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-4">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            <Button onClick={handleEdit}>Update Category</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCategory?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}